import { FormularioDialogService } from '../../../../auxiliares/servicios/formulario-dialog.service';
import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  ICreateEventoGenerico,
  IFilter,
  IQueryParam,
  ISirena,
  IUsuario,
  RangoHorario,
} from 'modelos/src';
import { debounceTime, distinctUntilChanged, firstValueFrom } from 'rxjs';
import { HelperService } from '../../../../auxiliares/servicios/helper.service';
import { DialogService } from '../../../../auxiliares/dialog/dialog.service';
import { MatDialog } from '@angular/material/dialog';
import { LoginService } from '../../../login/login.service';
import { SirenasHttpService } from '../../../../auxiliares/servicios/http/sirenas.service';
import { EventosGenericosHttpService } from '../../../../auxiliares/servicios/http/eventos-genericos.service';
import { Lista2Service } from '../../../../auxiliares/servicios/lista2.service';
import {
  CrearEditarUsuarioFuncionComponent,
  DataUsuarioFuncion,
} from '../../../standalone/usuarios-por-funcion/crear-editar-usuario-funcion/crear-editar-usuario-funcion.component';

interface IFiltrosForm {
  busqueda: FormControl<string | null>;
  idCliente: FormControl<string | null>;
  chipId: FormControl<string | null>;
  idModelo: FormControl<string | null>;
  estado: FormControl<'Online' | 'Offline' | null>;
  offlinePorTiempo: FormControl<'masUnDia' | 'menosUnDia' | null>;
  onlinePorTiempo: FormControl<'masUnDia' | 'menosUnDia' | null>;
}

interface IServicioForm {
  titulo: FormControl<string | null>;
  prioridad: FormControl<'Normal' | 'Alta' | 'Urgente'>;
  mensaje: FormControl<string | null>;
  solicitanteNombre: FormControl<string | null>;
  solicitanteTelefono: FormControl<string | null>;
  solicitanteEmail: FormControl<string | null>;
  disponibilidad: FormControl<RangoHorario[] | null>;
  idTecnicoAsignado: FormControl<string | null>;
}

@Component({
  selector: 'app-servicio-tecnico-masivo',
  templateUrl: './servicio-tecnico-masivo.component.html',
  styleUrl: './servicio-tecnico-masivo.component.scss',
  standalone: false,
})
export class ServicioTecnicoMasivoComponent implements OnInit {
  private formularios = inject(FormularioDialogService);
  public readonly LIMITE_SIRENAS = 50;

  public loading = signal(false);
  public buscando = signal(false);
  public creando = signal(false);

  private clientePropio = LoginService.getCliente();

  // Step 1: Filtros y resultados
  public filtrosForm!: FormGroup<IFiltrosForm>;
  public sirenas: ISirena[] = [];
  public sirenasSeleccionadas = new Set<string>();
  public buscado = signal(false);

  // Step 2: Datos del servicio
  public servicioForm!: FormGroup<IServicioForm>;

  // Opciones
  public tecnicos: IUsuario[] = [];

  public titulos: string[] = [
    'Baja',
    'Mantenimiento',
    'Cancelación de Servicio',
    'Fallo de comunicación',
    'Pruebas',
    'Retirar Equipo',
    'Solicitado por Atención al Cliente',
    'Cambio de ubicación',
  ];

  public prioridades = ['Normal', 'Alta', 'Urgente'] as const;
  /** Ítems de los ng-select (sin la opción "Todos": se limpia el campo) */
  public readonly prioridadesItems: string[] = [...this.prioridades];
  public readonly opcionesEstado: string[] = ['Online', 'Offline'];
  public readonly opcionesTiempo = [
    { valor: 'masUnDia', etiqueta: 'Más de 1 día' },
    { valor: 'menosUnDia', etiqueta: 'Menos de 1 día' },
  ];

  // Resultado masivo
  public resultados: { sirena: ISirena; ok: boolean; error?: string }[] = [];
  public mostrarResultados = signal(false);

  private sirenasService = inject(SirenasHttpService);
  private eventosService = inject(EventosGenericosHttpService);
  private lista2 = inject(Lista2Service);
  public helper = inject(HelperService);
  private dialogService = inject(DialogService);
  private dialog = inject(MatDialog);
  private destroyRef = inject(DestroyRef);

  private createForms() {
    this.filtrosForm = new FormGroup<IFiltrosForm>({
      busqueda: new FormControl<string | null>(null),
      idCliente: new FormControl<string | null>(null),
      chipId: new FormControl<string | null>(null),
      idModelo: new FormControl<string | null>(null),
      estado: new FormControl<'Online' | 'Offline' | null>(null),
      offlinePorTiempo: new FormControl<'masUnDia' | 'menosUnDia' | null>(null),
      onlinePorTiempo: new FormControl<'masUnDia' | 'menosUnDia' | null>(null),
    });

    this.servicioForm = new FormGroup<IServicioForm>({
      titulo: new FormControl<string | null>(null, [Validators.required]),
      prioridad: new FormControl<'Normal' | 'Alta' | 'Urgente'>('Normal', {
        nonNullable: true,
      }),
      mensaje: new FormControl<string | null>(null, [Validators.required]),
      solicitanteNombre: new FormControl<string | null>(null),
      solicitanteTelefono: new FormControl<string | null>(null),
      solicitanteEmail: new FormControl<string | null>(null, [
        Validators.email,
      ]),
      disponibilidad: new FormControl<RangoHorario[] | null>(null),
      idTecnicoAsignado: new FormControl<string | null>(null),
    });
  }

  public async buscarSirenas() {
    this.buscando.set(true);
    try {
      const filtros = this.filtrosForm.getRawValue();
      const filter: IFilter<ISirena> = {};
      if (filtros.busqueda) {
        (filter as never as Record<string, unknown>)['$or'] = [
          { chipId: { $regex: filtros.busqueda, $options: 'i' } },
          { tipo: { $regex: filtros.busqueda, $options: 'i' } },
          { direccion: { $regex: filtros.busqueda, $options: 'i' } },
        ];
      }
      if (filtros.idCliente) filter['idCliente'] = filtros.idCliente;
      if (filtros.chipId)
        filter['chipId'] = { $regex: filtros.chipId, $options: 'i' } as never;
      if (filtros.idModelo) filter['idModelo'] = filtros.idModelo;
      if (filtros.estado) filter['estado'] = filtros.estado;
      if (filtros.offlinePorTiempo) {
        const ayer = new Date();
        ayer.setDate(ayer.getDate() - 1);
        filter['offlineDesde'] =
          filtros.offlinePorTiempo === 'masUnDia'
            ? ({ $lte: ayer } as never)
            : ({ $gte: ayer } as never);
      }
      if (filtros.onlinePorTiempo) {
        const ayer = new Date();
        ayer.setDate(ayer.getDate() - 1);
        filter['onlineDesde'] =
          filtros.onlinePorTiempo === 'masUnDia'
            ? ({ $lte: ayer } as never)
            : ({ $gte: ayer } as never);
      }

      const query: IQueryParam = {
        filter: JSON.stringify(filter),
        populate: JSON.stringify([
          { path: 'cliente', select: 'nombre' },
          { path: 'modelo', select: 'marca modelo' },
        ]),
        sort: 'chipId',
        limit: 0,
        unlimited: true,
        includeChildren: true,
        childrenLevel: 0,
      };

      const result = await this.sirenasService.getFiltered(query);
      this.sirenas = result.datos;
      this.sirenasSeleccionadas = new Set();
      this.buscado.set(true);
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    } finally {
      this.buscando.set(false);
    }
  }

  public toggleSirena(id: string) {
    if (this.sirenasSeleccionadas.has(id)) {
      this.sirenasSeleccionadas.delete(id);
    } else {
      if (this.sirenasSeleccionadas.size >= this.LIMITE_SIRENAS) {
        this.helper.notifWarn(
          `Límite de ${this.LIMITE_SIRENAS} sirenas alcanzado. Deseleccioná alguna antes de agregar otra.`,
        );
        return;
      }
      this.sirenasSeleccionadas.add(id);
    }
  }

  public get seleccionadasCount(): number {
    return this.sirenasSeleccionadas.size;
  }

  public get todasSeleccionadas(): boolean {
    return (
      this.sirenas.length > 0 &&
      this.sirenas.every((s) => this.sirenasSeleccionadas.has(s._id!))
    );
  }

  public toggleSeleccionarTodas() {
    if (this.todasSeleccionadas) {
      this.sirenasSeleccionadas = new Set();
    } else {
      this.sirenasSeleccionadas = new Set(this.sirenas.map((s) => s._id!));
    }
  }

  public get resultadosOk(): number {
    return this.resultados.filter((r) => r.ok).length;
  }

  public get resultadosError(): number {
    return this.resultados.filter((r) => !r.ok).length;
  }

  public get todosOk(): boolean {
    return this.resultados.length > 0 && this.resultados.every((r) => r.ok);
  }

  public get todosError(): boolean {
    return this.resultados.length > 0 && this.resultados.every((r) => !r.ok);
  }

  public async onSubmit() {
    if (this.sirenasSeleccionadas.size === 0) {
      this.helper.notifWarn('Seleccioná al menos una sirena');
      return;
    }

    const res = await this.dialogService.confirm(
      'Crear Servicios Técnicos',
      `¿Querés crear ${this.sirenasSeleccionadas.size} servicio(s) técnico(s)?`,
    );
    if (!res) return;

    this.creando.set(true);
    this.resultados = [];

    const formValue = this.servicioForm.getRawValue();
    const idTecnico = formValue.idTecnicoAsignado;

    const sirenasFiltradas = this.sirenas.filter((s) =>
      this.sirenasSeleccionadas.has(s._id!),
    );

    const payloads: ICreateEventoGenerico[] = sirenasFiltradas.map(
      (sirena) =>
        ({
          tipoEvento: 'Evento Técnico Sirena',
          filtrador: 'Servicio Técnico',
          estado: idTecnico ? 'Asignado' : 'Pendiente',
          idEntidad: sirena._id!,
          idsClientesQuePuedenAtender:
            this.clientePropio?.config
              ?.idsClientesQuePuedenAtenderEventosTecnicos || [],
          valores: {
            titulo: formValue.titulo!,
            prioridad: formValue.prioridad,
            mensaje: formValue.mensaje!,
            categoria: 'Sirena',
            solicitante: {
              nombre: formValue.solicitanteNombre || undefined,
              telefono: formValue.solicitanteTelefono || undefined,
              email: formValue.solicitanteEmail || undefined,
            },
            disponibilidad: formValue.disponibilidad || undefined,
          } as never,
          detallesTecnicos: idTecnico
            ? ({ idTecnicoAsignado: idTecnico } as never)
            : undefined,
        }) as ICreateEventoGenerico,
    );

    try {
      await this.eventosService.createMany(payloads);
      this.resultados = sirenasFiltradas.map((sirena) => ({
        sirena,
        ok: true,
      }));
    } catch (error: unknown) {
      const msg =
        error instanceof Error ? error.message : JSON.stringify(error);
      this.resultados = sirenasFiltradas.map((sirena) => ({
        sirena,
        ok: false,
        error: msg,
      }));
    } finally {
      this.creando.set(false);
      this.mostrarResultados.set(true);
    }
  }

  public cerrarDialog() {
    const dialogRef = this.dialog.getDialogById(
      this.dialog.openDialogs[this.dialog.openDialogs.length - 1]?.id || '',
    );
    dialogRef?.close(true);
  }

  public async onAddTecnico() {
    const data: DataUsuarioFuncion = {
      editando: false,
      idCliente: this.clientePropio?._id || '',
      funcion: 'Técnico',
    };
    this.formularios.abrir(CrearEditarUsuarioFuncionComponent, {
      tamano: 'grande',
      data,
    });
  }

  private async listarTecnicos() {
    const query: IQueryParam = {
      sort: 'datosPersonales.nombre',
      limit: 0,
      select: 'datosPersonales usuario',
    };
    this.tecnicos = (await firstValueFrom(this.lista2.tecnicos(query))).datos;
  }

  async ngOnInit() {
    this.createForms();
    this.filtrosForm.controls.busqueda.valueChanges
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.buscarSirenas());
    await Promise.all([this.listarTecnicos()]);
  }
}
